import { Fragment, useState } from 'react';
import { Navigate, useOutletContext } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Pencil, Plus } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { money } from '@/lib/vendors';
import { todayIn } from '@/lib/reports';
import type { Program } from '@/lib/types';
import { cn, fmtDateTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/field';
import { Alert, Card, PageHeader, Spinner } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';

interface Row {
  id: string;
  category: string;
  approved: string;
  committed: string;
  actual: string;
  variance: string;
  months: Array<{ month: string; committed: string; actual: string; note: string | null }>;
}
export interface BudgetSummary {
  anyApproved: boolean;
  approved: string;
  committed: string;
  actual: string;
  variance: string;
}
interface Budget {
  rows: Row[];
  summary: BudgetSummary;
  selectedVendors: Array<{
    id: string;
    name: string;
    category: string;
    tco5: { total: string; incomplete: boolean } | null;
  }>;
  approvals: Array<{ at: string; category?: string; amount?: string; note?: string }>;
}

const monthName = (m: string) =>
  new Date(`${m}-01T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });

export function BudgetPage() {
  const program = useOutletContext<Program>();
  const allowed = program.capabilities.includes('budget.read');
  const canEdit = program.capabilities.includes('plan.manage');
  const q = useQuery({
    queryKey: ['budget', program.id],
    queryFn: () => api.get<Budget>(`/api/programs/${program.id}/budget`),
    enabled: allowed,
  });
  const [open, setOpen] = useState<string | null>(null);
  const [approving, setApproving] = useState<Row | null>(null);
  const [entry, setEntry] = useState<{
    row: Row;
    month: string;
    committed: string;
    actual: string;
    note: string;
  } | null>(null);
  if (!allowed) return <Navigate to={`/p/${program.id}/tracker`} replace />;
  if (!q.data) return <Spinner className="h-64" />;
  const b = q.data;
  const thisMonth = todayIn(program.timezone).slice(0, 7);

  return (
    <div className="mx-auto max-w-[1100px] space-y-5 px-6 py-6">
      <PageHeader
        title="Budget"
        description={`${program.clientShortName}'s implementation spend by category: approved against committed and actual. BlackWolf's fees are never recorded here.`}
      />
      {!b.summary.anyApproved && (
        <Alert tone="info" title="No approved budget yet">
          Approved amounts are entered once the Phase 2 Implementation Budget Estimate is accepted. Until then there is
          no plan to measure against, so nothing here reads as zero spend against a plan.
        </Alert>
      )}
      <div className="grid gap-3 sm:grid-cols-4">
        {(
          [
            ['Approved', b.summary.approved],
            ['Committed', b.summary.committed],
            ['Actual', b.summary.actual],
            ['Headroom', b.summary.variance],
          ] as const
        ).map(([label, v]) => (
          <Card key={label} className="p-4">
            <p className="text-xs text-muted">{label}</p>
            <p
              className={cn(
                'mt-1 text-lg font-semibold tabular-nums',
                label === 'Headroom' && Number(v) < 0 && 'text-bad',
              )}
            >
              {b.summary.anyApproved || label !== 'Headroom' ? money(v) : '-'}
            </p>
          </Card>
        ))}
      </div>

      <Card className="overflow-x-auto p-0">
        <table className="w-full text-[13px]">
          <thead className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted">
            <tr>
              <th className="w-8" />
              <th className="px-2 py-2 font-medium">Category</th>
              <th className="px-2 py-2 text-right font-medium">Approved</th>
              <th className="px-2 py-2 text-right font-medium">Committed</th>
              <th className="px-2 py-2 text-right font-medium">Actual</th>
              <th className="px-4 py-2 text-right font-medium">Headroom</th>
              {canEdit && <th className="w-24" />}
            </tr>
          </thead>
          <tbody>
            {b.rows.map((r) => (
              <Fragment key={r.id}>
                <tr className="border-b border-border">
                  <td className="pl-3">
                    <button
                      type="button"
                      aria-label={open === r.id ? 'Hide months' : 'Show months'}
                      onClick={() => setOpen(open === r.id ? null : r.id)}
                      className="text-muted hover:text-text"
                    >
                      <ChevronRight className={cn('size-4 transition', open === r.id && 'rotate-90')} />
                    </button>
                  </td>
                  <td className="px-2 py-2 font-medium">{r.category}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{Number(r.approved) ? money(r.approved) : '-'}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{money(r.committed)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{money(r.actual)}</td>
                  <td
                    className={cn(
                      'px-4 py-2 text-right tabular-nums',
                      Number(r.variance) < 0 && 'font-medium text-bad',
                    )}
                  >
                    {Number(r.approved) ? money(r.variance) : '-'}
                  </td>
                  {canEdit && (
                    <td className="px-2 py-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setApproving(r)}
                        aria-label={`Set approved amount for ${r.category}`}
                      >
                        <Pencil /> Approved
                      </Button>
                    </td>
                  )}
                </tr>
                {open === r.id && (
                  <tr className="border-b border-border bg-surface-2/40">
                    <td />
                    <td colSpan={canEdit ? 6 : 5} className="px-2 py-2">
                      {!r.months.length ? (
                        <p className="text-xs text-muted">No committed or actual spend recorded.</p>
                      ) : (
                        <table className="w-full text-xs">
                          <tbody>
                            {r.months.map((m) => (
                              <tr key={m.month}>
                                <td className="py-1">{monthName(m.month)}</td>
                                <td className="py-1 text-right tabular-nums">committed {money(m.committed)}</td>
                                <td className="py-1 text-right tabular-nums">actual {money(m.actual)}</td>
                                <td className="py-1 pl-4 text-muted">{m.note}</td>
                                {canEdit && (
                                  <td className="py-1 text-right">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() =>
                                        setEntry({
                                          row: r,
                                          month: m.month,
                                          committed: m.committed,
                                          actual: m.actual,
                                          note: m.note ?? '',
                                        })
                                      }
                                    >
                                      Edit
                                    </Button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="mt-1"
                          onClick={() => setEntry({ row: r, month: thisMonth, committed: '0', actual: '0', note: '' })}
                        >
                          <Plus /> Record a month
                        </Button>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </Card>

      {b.selectedVendors.length > 0 && (
        <Card className="p-0">
          <h2 className="border-b border-border px-4 py-2.5 text-sm font-semibold">Selected vendors (reference)</h2>
          <ul className="divide-y divide-border text-[13px]">
            {b.selectedVendors.map((v) => (
              <li key={v.id} className="flex items-center justify-between px-4 py-2">
                <span>
                  {v.name} <span className="text-xs text-muted">· {v.category}</span>
                </span>
                <span className="tabular-nums">
                  {v.tco5
                    ? `${money(v.tco5.total)} over 5 years${v.tco5.incomplete ? ' plus per-loan pricing' : ''}`
                    : 'No pricing'}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {b.approvals.length > 0 && (
        <Card className="p-0">
          <h2 className="border-b border-border px-4 py-2.5 text-sm font-semibold">Approval history</h2>
          <ul className="divide-y divide-border text-[13px]">
            {b.approvals.map((a, i) => (
              <li key={i} className="px-4 py-2">
                <span className="font-medium">{a.category}</span> set to {money(a.amount ?? 0)}{' '}
                <span className="text-muted">
                  · {a.note} · {fmtDateTime(a.at)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {approving && <ApproveDialog program={program} row={approving} onClose={() => setApproving(null)} />}
      {entry && <EntryDialog program={program} entry={entry} onClose={() => setEntry(null)} />}
    </div>
  );
}

function ApproveDialog({ program, row, onClose }: { program: Program; row: Row; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [amount, setAmount] = useState(Number(row.approved) ? row.approved : '');
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => api.patch(`/api/programs/${program.id}/budget/lines/${row.id}`, { approvedAmount: amount, note }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['budget', program.id] });
      void qc.invalidateQueries({ queryKey: ['executive', program.id] });
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Approved amount: ${row.category}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!amount || note.trim().length < 5}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Approved (USD)" htmlFor="ap-amt">
          <Input
            id="ap-amt"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
          />
        </Field>
        <Field
          label="Source"
          htmlFor="ap-note"
          hint="Cite the approval, such as the accepted Implementation Budget Estimate."
        >
          <Input id="ap-note" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function EntryDialog({
  program,
  entry,
  onClose,
}: {
  program: Program;
  entry: { row: Row; month: string; committed: string; actual: string; note: string };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState(entry);
  const save = useMutation({
    mutationFn: () =>
      api.put(`/api/programs/${program.id}/budget/lines/${f.row.id}/months/${f.month}`, {
        committed: f.committed || '0',
        actual: f.actual || '0',
        note: f.note || null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['budget', program.id] });
      void qc.invalidateQueries({ queryKey: ['executive', program.id] });
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={`${entry.row.category}: a month of spend`}
      description="Committed is contracted but not yet paid; actual is paid. Both are totals for the month."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-3 gap-3">
        <Field label="Month" htmlFor="en-month">
          <Input id="en-month" type="month" value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })} />
        </Field>
        <Field label="Committed" htmlFor="en-com">
          <Input
            id="en-com"
            inputMode="decimal"
            value={f.committed}
            onChange={(e) => setF({ ...f, committed: e.target.value.replace(/[^\d.]/g, '') })}
          />
        </Field>
        <Field label="Actual" htmlFor="en-act">
          <Input
            id="en-act"
            inputMode="decimal"
            value={f.actual}
            onChange={(e) => setF({ ...f, actual: e.target.value.replace(/[^\d.]/g, '') })}
          />
        </Field>
        <Field label="Note" htmlFor="en-note" className="col-span-3">
          <Input id="en-note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Field>
      </div>
    </Dialog>
  );
}
